import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { FormArray, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatSliderModule } from '@angular/material/slider';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { IonButton, IonLabel, IonSegment, IonSegmentButton, IonToolbar, IonIcon, IonContent } from '@ionic/angular/standalone';
import { IAdvancedDevicesSettings, ISimpleDevicesSettings } from '../../models/be/devicesSettings';
import { generaOrari } from '../../utils/generic';
import { addIcons } from 'ionicons';
import { remove, close, arrowForward, checkmarkCircleOutline } from 'ionicons/icons';
import { ToastService } from '../../../core/services/utils/toast.service';
import { IonicColors } from '../../enums/ionicColors.enum';
import { checkForOverlapsAdvanced } from '../../utils/check.utils';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

type NotificationMode = "always_active" | "disabled" | "scheduled";

interface IDeviceSettingData {
  id: string;
  schedules: { start_time: string, end_time: string }[];
  bedExitDelay: number;
  bathroomEntranceDelay: number;
  name: string;
  notificationMode: NotificationMode;
  disableBedInAlert: boolean;
  isBedSetting: boolean;
  isBathroomSetting: boolean;
}

@Component({
  selector: 'timingComponentsGroup',
  templateUrl: './timingComponentsGroup.component.html',
  styleUrls: ['./timingComponentsGroup.component.scss'],
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatGridListModule,
    MatFormFieldModule,
    MatSelectModule,
    TranslateModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonToggleModule,
    MatSliderModule,
    IonSegment,
    IonLabel,
    IonIcon,
    IonSegmentButton,
    FormsModule,
    MatSlideToggleModule
  ],
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        subscriptSizing: 'dynamic'
      }
    }
  ]
})
export class TimingComponentsGroup implements OnInit {

  /**
   * @description FormGroup che regola tutti i campi della form nel component 
   */
  public formGroup!: FormGroup;
  public formArray!: FormArray;
  public hasError: boolean = false;
  public isHandleTouched: boolean = false;
  public hoursList: string[] = generaOrari(15);
  public readonly bathroomDelayOptions = Array.from({ length: 60 }, (_, index) => index + 1);
  @Output() onSubmitData = new EventEmitter();
  @Input() deviceSettings!: ISimpleDevicesSettings | IAdvancedDevicesSettings;
  @Input() deviceSettingsMode!: 'simple' | 'advanced';
  @Input() isMobile: boolean = true;
  @Input() beds: {
    id: string;
    name: string;
  }[] = [];
  public deviceSettingsData: IDeviceSettingData[] = [];
  constructor(
    private toastService: ToastService,
  ) {
    addIcons({ remove, close, arrowForward, checkmarkCircleOutline });
  }

  ngOnInit() {
    this.initializer();
  }

  initializer() {
    if (this.deviceSettingsMode === 'simple') {
      this.deviceSettings = this.deviceSettings as ISimpleDevicesSettings;
      this.deviceSettingsData = [{
        id: 'all',
        name: 'TUTTE LE ALLERTE',
        schedules: this.deviceSettings.settings?.schedules?.length ? this.deviceSettings.settings.schedules : [{ start_time: "00:00", end_time: "00:00" }],
        bedExitDelay: this.deviceSettings.settings?.bedExitDelay ?? 0,
        bathroomEntranceDelay: this.deviceSettings.settings?.bathroomEntranceDelay ?? 0,
        notificationMode: this.deviceSettings?.settings?.notificationMode || 'scheduled',
        disableBedInAlert: false,
        isBedSetting: false,
        isBathroomSetting: false
      }]
    } else {
      this.deviceSettings = this.deviceSettings as IAdvancedDevicesSettings;
      const bathroomEvents = this.deviceSettings.settings?.bathroom_events ?? {
        notificationMode: 'always_active' as const,
        bathroomEntranceDelay: 0,
        schedules: []
      };
      this.deviceSettingsData = [{
        id: 'fall',
        name: 'CADUTA',
        schedules: this.deviceSettings.settings?.fall?.schedules.length ? this.deviceSettings.settings.fall.schedules : [{ start_time: "00:00", end_time: "00:00" }],
        bedExitDelay: this.deviceSettings.settings?.fall?.bedExitDelay || 0,
        bathroomEntranceDelay: 0,
        notificationMode: this.deviceSettings.settings?.fall?.notificationMode || 'scheduled',
        disableBedInAlert: false,
        isBedSetting: false,
        isBathroomSetting: false
      }, {
        id: 'room',
        name: 'ENTRATA/USCITA STANZA',
        schedules: this.deviceSettings.settings?.room_events?.schedules.length ? this.deviceSettings.settings.room_events.schedules : [{ start_time: "00:00", end_time: "00:00" }],
        bedExitDelay: this.deviceSettings.settings?.room_events?.bedExitDelay || 0,
        bathroomEntranceDelay: 0,
        notificationMode: this.deviceSettings.settings?.room_events?.notificationMode || 'scheduled',
        disableBedInAlert: false,
        isBedSetting: false,
        isBathroomSetting: false
      }, {
        id: 'bathroom',
        name: 'INGRESSO BAGNO',
        schedules: bathroomEvents.schedules.length ? bathroomEvents.schedules : [],
        bedExitDelay: 0,
        bathroomEntranceDelay: bathroomEvents.bathroomEntranceDelay ?? 0,
        notificationMode: bathroomEvents.notificationMode,
        disableBedInAlert: false,
        isBedSetting: false,
        isBathroomSetting: true
      }]
      this.beds.forEach((bed) => {
        this.deviceSettings = this.deviceSettings as IAdvancedDevicesSettings;
        const currDevSettings = this.deviceSettings.settings?.bed_events?.[bed.id];
        this.deviceSettingsData.push({
          id: bed.id,
          name: 'ENTRATA/USCITA LETTO (' + bed.name + ')',
          schedules: currDevSettings?.schedules?.length ? currDevSettings.schedules : [{
            start_time: "00:00",
            end_time: "00:00"
          }],
          bedExitDelay: currDevSettings?.bedExitDelay || 0,
          bathroomEntranceDelay: 0,
          notificationMode: currDevSettings?.notificationMode || 'scheduled',
          disableBedInAlert: currDevSettings?.disableBedInAlert ?? false,
          isBedSetting: true,
          isBathroomSetting: false
        })
      });
    }
  }

  /**
   * TEMPORANEO
   * @deprecated
   */
  formatLabel(value: number): string {
    return `${Math.floor(value / 2).toString().padStart(2, '0')}:${value % 2 === 0 ? '00' : '30'}`;
  }

  submitData() {
    if (this.deviceSettingsData.some((setting) => (setting.notificationMode === 'scheduled') && checkForOverlapsAdvanced(setting.schedules))) {
      this.toastService.showToast("Fascie orarie non conformi! Modificare e riprovare", IonicColors.RED)
    } else {
      if (this.deviceSettingsMode === 'simple') {
        const targetSetting = this.deviceSettingsData.find((sett) => sett.id === 'all');
        if (!targetSetting) {
          this.toastService.showToast("Errore nel salvataggio della temporizzazione, riprovare");
          return;
        } else {
          (this.deviceSettings as ISimpleDevicesSettings).settings = {
            bedExitDelay: targetSetting.bedExitDelay,
            bathroomEntranceDelay: targetSetting.bathroomEntranceDelay,
            notificationMode: targetSetting.notificationMode,
            schedules: targetSetting.schedules
          }
        }
      } else {
        const fallSetting = this.deviceSettingsData.find((sett) => sett.id === 'fall');
        const roomSetting = this.deviceSettingsData.find((sett) => sett.id === 'room');
        const bathroomSetting = this.deviceSettingsData.find((sett) => sett.id === 'bathroom');
        const bedsSettings = this.deviceSettingsData.filter((sett) => sett.isBedSetting);
        if (!fallSetting || !roomSetting || !bathroomSetting) {
          this.toastService.showToast("Errore nel salvataggio della temporizzazione, riprovare");
          return;
        } else {
          (this.deviceSettings as IAdvancedDevicesSettings).beds = this.beds.map((bed) => ({ bedId: bed.id, bedName: bed.name }));
          (this.deviceSettings as IAdvancedDevicesSettings).settings = {
            fall: {
              bedExitDelay: fallSetting.bedExitDelay || 0,
              notificationMode: fallSetting.notificationMode || 'always_active',
              schedules: fallSetting.schedules || []
            },
            room_events: {
              bedExitDelay: roomSetting.bedExitDelay || 0,
              notificationMode: roomSetting.notificationMode || 'always_active',
              schedules: roomSetting.schedules || []
            },
            bathroom_events: {
              bathroomEntranceDelay: bathroomSetting.bathroomEntranceDelay ?? 0,
              notificationMode: bathroomSetting.notificationMode || 'always_active',
              schedules: bathroomSetting.schedules || []
            },
            bed_events: bedsSettings.reduce((acc, curr) => {
              acc[curr.id] = {
                bedExitDelay: curr.bedExitDelay || 0,
                notificationMode: curr.notificationMode || 'always_active',
                schedules: curr.schedules || [],
                disableBedInAlert: curr.disableBedInAlert ?? false
              }
              return acc;
            }, {} as any)
          }
        }
      }
      this.onSubmitData.emit({ deviceSettings: this.deviceSettings, deviceSettingsMode: this.deviceSettingsMode })
    }
  }
}
